<script lang="ts">
  import { z } from 'zod'
  import { Eye, EyeClosed, Lock, Unlock } from '@lucide/svelte'
  import IconButton from '../icon-button/IconButton.svelte'
  import ContextMenu from '../context-menu/ContextMenu.svelte'
  import ContextMenuTrigger from '../context-menu/ContextMenuTrigger.svelte'
  import { setCanvasSelection } from '../../stores/canvas-selection'
  import { validatedForm } from '../../hooks/forms'
  import useGroupQueries from '../../routes/project/hooks/useGroupQueries'
  import Pane from '../pane/Pane.svelte'
  import PaneHead from '../pane/PaneHead.svelte'
  import type { MouseEventHandler } from 'svelte/elements'
  import useCanvasSelection from '../../hooks/canvas-selection'
  import PaneContent from '../pane/PaneContent.svelte'
  import { ThreeItemKeys } from '../../constants/three'
  import File from '../printer-pane/components/File.svelte'
  import GroupContextMenuContent from '../group-context-menu-content/GroupContextMenuContent.svelte'
  import RenameForm from '../printer-pane/components/RenameForm.svelte'
  import { queryReactiveAccessor } from '../../helpers/tanstack-query'
  import SoloFilePane from './SoloFilePane.svelte'

  const { id } = $props<{ id: number }>()

  const { object, multiSelection, upsert } = useCanvasSelection()

  const {
    focusCamera,
    hideMutation,
    lockMutation,
    renameMutation,
    showMutation,
    unlockMutation,
    useGroup,
    useGroupFiles,
  } = useGroupQueries()

  /* svelte-ignore state_referenced_locally */
  const groupQuery = useGroup(id)
  /* svelte-ignore state_referenced_locally */
  const groupFilesQuery = useGroupFiles(id)

  let group = $derived.by(queryReactiveAccessor(groupQuery))
  let groupFiles = $derived.by(queryReactiveAccessor(groupFilesQuery))
  let visible = $derived.by(queryReactiveAccessor(groupQuery, data => data?.visible))
  let locked = $derived.by(queryReactiveAccessor(groupQuery, data => data?.userData[ThreeItemKeys.LOCKED]))

  const formElement = $state<HTMLFormElement>()

  let editing = $state(false)

  let selected = $derived(
    $object?.uuid === group?.uuid || $multiSelection.some(item => item.uuid === group?.uuid),
  )

  const renameSchema = z.object({
    name: z.string(),
  })

  const { setValue } = validatedForm({
    zodSchema: renameSchema,
    submitFunction: values => renameMutation.mutateAsync({ groupId: id, name: values.name }),
    onSuccess: () => {
      editing = false
    },
  })

  $effect(() => {
    if (editing && group) {
      setValue('name', group.name)
      setCanvasSelection(group)
      const inputElement = formElement?.elements.namedItem(
        'group-name',
      ) as HTMLInputElement
      inputElement.focus()
      inputElement.select()
    }
  })

  const addGroupToSelection: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (!group) return
    upsert(group, event && (event.ctrlKey || event.metaKey))
  }

  async function handleRename(name: string): Promise<void> {
    await renameMutation.mutateAsync({ groupId: id, name })
  }
</script>

{#if group && groupFiles && groupFiles.length === 1}
  <SoloFilePane {id} />
{:else if group && groupFiles && groupFiles.length > 1}
  <div class="container">
    <Pane key={`group-${group.id}`}>
      <ContextMenu>
        <ContextMenuTrigger>
          <PaneHead
            class="head"
            containerClassName="head-container"
            data-hidden={!visible}
            data-selected={selected}
          >
            {#if editing}
              <RenameForm
                name={`group-${group.id}`}
                defaultValue={group.name}
                onRename={handleRename}
                onSuccess={() => {
                  editing = false
                }}
              />
            {:else}
              <button
                class="name"
                onclick={addGroupToSelection}
                ondblclick={(e) => {
                  addGroupToSelection(e)
                  focusCamera(id)
                }}
              >
                {group.name}
              </button>
            {/if}

            {#if locked}
              <IconButton onclick={() => unlockMutation.mutate(id)}>
                <Lock />
              </IconButton>
            {:else}
              <IconButton onclick={() => lockMutation.mutate(id)}>
                <Unlock />
              </IconButton>
            {/if}
            {#if visible}
              <IconButton onclick={() => hideMutation.mutate(id)}>
                <Eye />
              </IconButton>
            {:else}
              <IconButton onclick={() => showMutation.mutate(id)}>
                <EyeClosed />
              </IconButton>
            {/if}
          </PaneHead>
        </ContextMenuTrigger>
        <GroupContextMenuContent
          groupId={group.uuid}
          onRename={() => {
            editing = true
          }}
        />
      </ContextMenu>
      <PaneContent>
        {#each groupFiles || [] as file (file.uuid)}
          <File fileId={file.uuid} />
        {/each}
      </PaneContent>
    </Pane>
  </div>
{/if}

<style lang="scss">
  .container {
    background-color: var(--printer-pane-background);

    .name {
      background-color: transparent;
      border: none;
      outline: none;
      color: currentColor;
      min-height: 3rem;
      width: 100%;
      text-align: start;
      padding: 0;
      cursor: pointer;

      &:focus-visible {
        outline: 1px solid var(--stroke);
        outline-offset: -1px;
      }
    }

    :global(.head) {
      justify-content: space-between;
    }

    :global(.head[data-hidden="true"]) {
      filter: opacity(0.5);
    }

    :global(.head-container:has([data-selected="true"])) {
      background-color: var(--printer-pane-active-background);
    }
  }
</style>
